import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconSettings, IconMapPin, IconCalendar, IconLock, IconDiamond, IconShield } from '@tabler/icons-react';
import { db, storage, functions } from '../firebase';
import Footer from '../components/Footer';
import { doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useAdminView } from '../contexts/AdminViewContext';
import StableAvatar from '../components/StableAvatar';
import './ProfilePage.css';

const DEFAULT_AVATAR = '/default-avatar.png';

const ProfilePage = ({ user }) => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAdminView();
  const { t } = useTranslation();
  const [profileUser, setProfileUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [stats, setStats] = useState({ posts: 0 });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [lastVisibleDoc, setLastVisibleDoc] = useState(null);
  const loadMoreRef = useRef(null);
  const profileCacheRef = useRef(new Map());
  const postsRef = useRef([]);
  const getProfilePublicCallable = useMemo(
    () => httpsCallable(functions, 'getUserProfilePublic'),
    [functions]
  );
  const dedupePosts = useCallback((items) => {
    if (!Array.isArray(items)) return [];
    if (items.length <= 1) return items.filter(Boolean);
    const map = new Map();
    for (const item of items) {
      if (!item?.id || map.has(item.id)) continue;
      map.set(item.id, item);
    }
    return Array.from(map.values());
  }, []);
  const resolvedUid = useMemo(() => {
    if (!uid || uid === 'me') {
      return user?.uid || null;
    }
    return uid;
  }, [uid, user?.uid]);

  const isOwnProfile = Boolean(user?.uid && resolvedUid && user.uid === resolvedUid);

  useEffect(() => {
    postsRef.current = posts;
  }, [posts]);

  const toDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? null : new Date(parsed);
    }
    if (typeof value === 'object' && value._seconds !== undefined) {
      const seconds = Number(value._seconds) || 0;
      const nanos = Number(value._nanoseconds || value.nanoseconds || 0);
      return new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
    }
    return null;
  };

  useEffect(() => {
    if (!resolvedUid) {
      if (!uid || uid === 'me') {
        setIsLoading(false);
      }
      return;
    }
    loadProfile(resolvedUid);
  }, [resolvedUid, user?.uid]);

  const loadProfile = async (targetUid) => {
    if (!targetUid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const profileCache = profileCacheRef.current;
      const hasCachedProfile = profileCache.has(targetUid);
      let sanitizedProfile = hasCachedProfile ? profileCache.get(targetUid) : undefined;

      if (!hasCachedProfile || (isOwnProfile && sanitizedProfile === null)) {
        if (isOwnProfile) {
          try {
            const userDocRef = doc(db, 'users', targetUid);
            const userDoc = await getDoc(userDocRef);

            if (userDoc.exists()) {
              const userData = userDoc.data() || {};
              const joinedDateValue = toDateValue(userData.createdAt);
              sanitizedProfile = {
                userId: targetUid,
                username: typeof userData.username === 'string' ? userData.username : null,
                displayName: typeof userData.displayName === 'string'
                  ? userData.displayName
                  : (typeof userData.username === 'string' ? userData.username : null),
                photoURL: typeof userData.photoURL === 'string' ? userData.photoURL : null,
                bio: typeof userData.bio === 'string' ? userData.bio : null,
                location: typeof userData.location === 'string' ? userData.location : null,
                joinedAt: joinedDateValue ? joinedDateValue.getTime() : null,
              };
            } else {
              sanitizedProfile = null;
            }
          } catch (error) {
            console.warn('Error loading profile from Firestore:', error);
            sanitizedProfile = null;
          }
        } else {
          try {
            const profileResponse = await getProfilePublicCallable({ userId: targetUid });
            const profileDataRaw = profileResponse?.data;
            sanitizedProfile = profileDataRaw && typeof profileDataRaw === 'object' ? profileDataRaw : null;
          } catch (error) {
            console.warn('Error loading profile via Cloud Function:', error);
            sanitizedProfile = null;
          }
        }

        profileCache.set(targetUid, sanitizedProfile);
      }

      let profileInfo = null;

      if (sanitizedProfile && typeof sanitizedProfile === 'object') {
        const locationValue = typeof sanitizedProfile.location === 'string' ? sanitizedProfile.location : '';
        profileInfo = {
          id: targetUid,
          userId: targetUid,
          username: sanitizedProfile.username || sanitizedProfile.displayName || targetUid,
          displayName: sanitizedProfile.displayName || sanitizedProfile.username || 'Mythic Explorer',
          avatar: sanitizedProfile.photoURL || DEFAULT_AVATAR,
          bio: sanitizedProfile.bio || '',
          location: locationValue,
          joinedDate: sanitizedProfile.joinedAt ? new Date(sanitizedProfile.joinedAt) : null,
        };
      }

      if (!profileInfo) {
        if (isOwnProfile && user) {
          profileInfo = {
            id: targetUid,
            userId: targetUid,
            username: user.email?.split('@')[0] || 'me',
            displayName: user.displayName || user.email || 'My Profile',
            avatar: user.photoURL || DEFAULT_AVATAR,
            bio: '',
            location: '',
            joinedDate: new Date(user.metadata?.creationTime || Date.now())
          };
        } else {
          profileInfo = {
            id: targetUid,
            userId: targetUid,
            username: targetUid,
            displayName: 'Mythic Explorer',
            avatar: DEFAULT_AVATAR,
            bio: '',
            location: '',
            joinedDate: null
          };
        }
      }

      setProfileUser(profileInfo);
      setIsLoading(false);

      await loadPosts({ userId: targetUid, profileInfo, includePrivate: isOwnProfile, reset: true });

    } catch (error) {
      console.error('Error loading profile:', error);
      setProfileUser(null);
      setPosts([]);
      setIsLoading(false);
    }
  };

  const POSTS_PAGE_SIZE = 12;

  const loadPosts = async ({ userId, profileInfo, includePrivate, reset = false }) => {
    if (!userId) return;

    try {
      if (reset) {
        setIsLoadingPosts(true);
        setIsLoadingMore(false);
        setPosts([]);
        setStats({ posts: 0 });
        setLastVisibleDoc(null);
        setHasMorePosts(true);
      } else {
        setIsLoadingMore(true);
      }

      const getUserMaps = httpsCallable(functions, 'getUserPublicMaps');
      const response = await getUserMaps({
        userId,
        includePrivate,
        lastDocId: reset ? null : lastVisibleDoc,
        limit: POSTS_PAGE_SIZE
      });

      const { maps = [], hasMore = false, lastDocId: nextCursor = null } = response.data || {};
      const defaultAvatar = profileInfo?.avatar || DEFAULT_AVATAR;

      let postsWithImages = await Promise.all(
        maps.map(async (map) => {
          let thumbnailUrl = null;
          const imagePathToTry = map.generatedImagePath || map.originalImagePath;
          if (imagePathToTry) {
            try {
              thumbnailUrl = await getDownloadURL(ref(storage, imagePathToTry));
            } catch (e) {
              console.warn(`Failed to get thumbnail URL for ${imagePathToTry}:`, e.message);
            }
          }

          return {
            id: map.id,
            userId: map.userId,
            user: {
              id: map.userId,
              username: profileInfo?.username || map.userUsername || 'user',
              displayName: profileInfo?.displayName || map.userDisplayName || 'User',
              avatar: profileInfo?.avatar || map.userAvatar || defaultAvatar
            },
            images: [thumbnailUrl || '/demo-maps/itoshima-ride.png'],
            caption: map.activityName || 'Untitled Map',
            activityType: (map.activityType || 'ride').toLowerCase(),
            createdAt: toDateValue(map.createdAt) || new Date(),
            location: map.location || null,
            style: map.style || 'Custom',
            isPublic: map.isPublic !== false,
            stravaActivityId: map.stravaId,
            stravaStats: map.stravaStats || null,
          };
        })
      );

      postsWithImages = dedupePosts(postsWithImages);

      setPosts(prev => {
        const combinedBase = reset ? postsWithImages : [...prev, ...postsWithImages];
        const deduped = dedupePosts(combinedBase);
        setStats({ posts: deduped.length });
        return deduped;
      });
      setLastVisibleDoc(nextCursor);
      setHasMorePosts(hasMore);

    } catch (error) {
      console.error('Error loading posts:', error);
      if (reset) {
        setPosts([]);
        setStats({ posts: 0 });
      }
      setHasMorePosts(false);
    } finally {
      if (reset) {
        setIsLoadingPosts(false);
      }
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!hasMorePosts || isLoadingPosts || isLoadingMore) return;
    if (!profileUser || !resolvedUid) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadPosts({
          userId: resolvedUid,
          profileInfo: profileUser,
          includePrivate: isOwnProfile,
          reset: false
        });
      }
    }, { rootMargin: '200px 0px', threshold: 0 });

    const node = loadMoreRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  }, [hasMorePosts, isLoadingPosts, isLoadingMore, posts.length, profileUser, resolvedUid, isOwnProfile]);

  const handlePostClick = (post) => {
    if (isOwnProfile) {
      navigate(`/manage-post/${post.id}`);
    } else {
      navigate(`/post/${post.id}`);
    }
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const handleDiamonds = () => {
    navigate('/diamonds');
  };

  const handleAdmin = () => {
    navigate('/admin-menu');
  };

  const displayedPosts = isOwnProfile ? posts : posts.filter(post => post.isPublic);

  if (isLoading) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="spinner"></div>
          <p>{t('profilePage.loadingProfile', 'Loading profile...')}</p>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="profile-page">
        <div className="profile-not-found">
          <h2>{t('profilePage.userNotFound', 'User not found')}</h2>
          <p>{t('profilePage.userNotFoundDesc', "The profile you're looking for doesn't exist.")}</p>
          <button onClick={() => navigate('/')}>{t('profilePage.goHome', 'Go Home')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
        {/* Profile Header */}
        <div className="profile-header">
        <div className="profile-header-content">
          <div className="profile-avatar">
            <StableAvatar
              identityKey={profileUser.userId || profileUser.id}
              src={profileUser.avatar}
              alt={profileUser.displayName}
            />
          </div>
          
          <div className="profile-info">
            <div className="profile-top">
              <div className="profile-top-row">
                <div className="profile-name-section">
                  <h1>{profileUser.displayName}</h1>
                  <span className="profile-username">@{profileUser.username}</span>
                </div>
                
                {isOwnProfile && (
            <div className="profile-actions">
                    <button className="btn-icon" onClick={handleDiamonds}>
                      <IconDiamond size={20} />
                    </button>
                    <button className="btn-icon" onClick={handleSettings}>
                <IconSettings size={20} />
              </button>
                  </div>
                )}
              </div>
              
              {isOwnProfile && isAdmin && (
                <button className="btn-admin" onClick={handleAdmin}>
                  <IconShield size={18} />
                  {t('profilePage.admin', 'Admin')}
                </button>
              )}
            </div>

            <div className="profile-stats">
              <div className="stat">
                <span className="stat-value">{stats.posts}</span>
                <span className="stat-label">{t('profilePage.posts', 'posts')}</span>
              </div>
            </div>

            {profileUser.bio && (
              <p className="profile-bio">{profileUser.bio}</p>
            )}

            <div className="profile-meta">
              {profileUser.location && (
                <span className="meta-item">
                  <IconMapPin size={16} />
                  {profileUser.location}
                </span>
              )}
              {profileUser.joinedDate && (
                <span className="meta-item">
                  <IconCalendar size={16} />
                  {t('profilePage.joined', 'Joined')} {profileUser.joinedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
              )}
              {isOwnProfile && isAdmin && (
                <span className="meta-item" style={{ opacity: 0.6, fontSize: '0.85em' }}>
                  v{process.env.REACT_APP_VERSION || require('../../package.json').version}
                </span>
              )}
            </div>
          </div>
        </div>

        </div>

        {/* Posts Grid */}
      {isLoadingPosts && displayedPosts.length === 0 ? (
        <div className="profile-loading">
          <div className="spinner"></div>
          <p>{t('profilePage.loadingMaps', 'Loading maps...')}</p>
        </div>
      ) : displayedPosts.length > 0 ? (
        <div className="profile-grid">
          {displayedPosts.map((post) => (
            <div
              key={post.id}
              className="grid-item"
              onClick={() => handlePostClick(post)}
            >
              <img src={post.images[0]} alt={post.caption} />
              {!post.isPublic && isOwnProfile && (
                <div className="private-badge">
                  <IconLock size={16} />
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="profile-empty">
            <h3>{t('profilePage.noPosts', 'No posts yet')}</h3>
            <p>
              {isOwnProfile
                ? t('profilePage.noPostsOwn', "You haven't created any mythic maps yet. Start your journey!")
                : t('profilePage.noPostsOther', "This user hasn't shared any public maps yet.")}
            </p>
            {isOwnProfile && (
              <button className="btn-primary" onClick={() => navigate('/create')}>
                {t('profilePage.createFirstMap', 'Create Your First Map')}
              </button>
            )}
        </div>
      )}
      {hasMorePosts && (
        <div ref={loadMoreRef} className="load-more-trigger">
          {isLoadingMore && <div className="spinner small"></div>}
        </div>
      )}
      <Footer />
    </div>
  );
};

export default ProfilePage;
